import { useEffect, useRef, useState } from 'react';
import type { Claim, ClaimSummary, Mode } from '../types';
import { TRAINING_GOAL, formatMoney } from '../utils';
import { recordLearning } from '../store';
import { Tick } from './common';
import type { LearnEdit } from './Detail';
import { Link } from 'react-router-dom';
import { PMS } from '@/features/shared/pms';

function learningText(claim: Claim, learn: LearnEdit): { did: string; will: string; scope: string } {
  if (claim.learningOverride && learn.kind === 'narrative') {
    return claim.learningOverride;
  }
  const payer = claim.payer.short;
  const group = claim.procedureGroup;
  switch (learn.kind) {
    case 'undo':
      return {
        did: `You undid “${learn.changeTitle}”.`,
        will: `For ${payer} ${group} claims, ELVA will stop doing this.`,
        scope: `${payer} ${group} claims`,
      };
    case 'attachment':
      return {
        did: `You added ${learn.name}.`,
        will: `For ${payer} ${group} claims, ELVA will attach this kind of document too.`,
        scope: `${payer} ${group} claims`,
      };
    case 'narrative':
      return {
        did: 'You edited the narrative.',
        will: `For ${payer} ${group} claims, ELVA will write narratives closer to yours.`,
        scope: `${payer} ${group} claims`,
      };
    case 'procedure':
      return {
        did: `You changed ${learn.code} ${learn.field}.`,
        will: `For ${payer} claims, ELVA will check this before preparing the claim.`,
        scope: `${payer} claims`,
      };
  }
}

function LearningCard({ claim, learn }: { claim: Claim; learn: LearnEdit }) {
  const [answered, setAnswered] = useState<null | 'right' | 'one_off'>(null);
  const { did, will, scope } = learningText(claim, learn);
  return (
    <section className="clm-learning" aria-label="ELVA learned from your edit">
      <span className="clm-eyebrow">ELVA learned from your edit</span>
      <p style={{ margin: 0 }}>
        {did} <strong>{will}</strong>
      </p>
      {answered ? (
        <p className="clm-meta" style={{ margin: 0 }}>
          {answered === 'right' ? `Saved. ELVA will do this for ${scope}.` : 'Got it. This stays with this claim.'}
        </p>
      ) : (
        <div className="clm-dialog-actions">
          <button
            type="button"
            className="clm-btn clm-btn-sm"
            onClick={() => {
              setAnswered('right');
              void recordLearning(claim.id, 'right');
            }}
          >
            That’s right
          </button>
          <button
            type="button"
            className="clm-btn clm-btn-sm clm-btn-ghost"
            onClick={() => {
              setAnswered('one_off');
              void recordLearning(claim.id, 'one_off');
            }}
          >
            This was a one-off
          </button>
        </div>
      )}
    </section>
  );
}

export function ConfirmedState({
  claim,
  mode,
  confirmed,
  learn,
  next,
  onOpenNext,
}: {
  claim: Claim;
  mode: Mode;
  confirmed: number;
  learn: LearnEdit | null;
  next: ClaimSummary | null;
  onOpenNext: () => void;
}) {
  const headingRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <main className="clm-detail">
      <div className="clm-confirmed">
        <div className="clm-confirmed-panel" role="status">
          <Tick className="clm-tick clm-tick-white" />
          <div>
            <p className="clm-confirmed-title" tabIndex={-1} ref={headingRef}>
              {mode === 'autopilot'
                ? `${claim.patient} confirmed`
                : `${claim.patient} confirmed · ${String(confirmed)} of ${String(TRAINING_GOAL)}`}
            </p>
            <p style={{ margin: 0 }}>
              ELVA is updating the claim in {PMS} and submitting it to {claim.payer.short}. Track it in{' '}
              <Link to={`/claims/record/${claim.id}`} className="clm-src" style={{ textDecoration: 'underline' }}>
                the claim record
              </Link>
              .
            </p>
          </div>
        </div>

        {learn ? <LearningCard claim={claim} learn={learn} /> : null}

        <div className="clm-next">
          {next ? (
            <>
              <span className="clm-meta">
                Next: {next.patient} · {next.payerShort} · {formatMoney(next.billed)}
              </span>
              <button type="button" className="clm-btn clm-btn-primary" onClick={onOpenNext}>
                Open next claim
              </button>
            </>
          ) : (
            <span className="clm-meta">That’s every claim for now.</span>
          )}
        </div>
      </div>
    </main>
  );
}
